import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { formatMonthName } from '../../utils/dateFormatter';
import { fill, nounFor } from './kpiText';

/**
 * The two monthly KPIs on one sheet. Last month is closed, so it carries the weight; this month is still
 * moving, so it reads smaller. No target or colour: this page is for monitoring for now
 * (tasks/ops-performance-kpi-restructure.md F2).
 */

const NO_READING = '—';
const hasReading = ({ value }) => value !== null && value !== undefined;

const KPI_VIEW = {
  rating: {
    title: 'opsPerformance.kpi.rating',
    figure: (period) => (hasReading(period) ? period.value.toFixed(1) : NO_READING),
    lines: (period, text) => [
      period.reviews > 0
        ? fill(text.t('opsPerformance.kpi.reviews'), { count: period.reviews, noun: text.review(period.reviews) })
        : text.t('opsPerformance.kpi.noReviews')
    ],
    note: (kpi, text, figure) => fill(text.t('opsPerformance.kpi.ratingNote'), {
      value: figure(kpi.allTime), count: kpi.allTime.reviews, noun: text.review(kpi.allTime.reviews)
    })
  },
  occupancy: {
    title: 'opsPerformance.kpi.occupancy',
    figure: (period) => (hasReading(period) ? `${period.value}%` : NO_READING),
    // Names every night it could not treat as plainly sold or open.
    lines: (period, text) => [
      fill(text.t('opsPerformance.kpi.nights'), { booked: period.booked, sellable: period.sellable, noun: text.night(period.sellable) }),
      period.closed > 0 && fill(text.t('opsPerformance.kpi.closed'), { count: period.closed, noun: text.night(period.closed) }),
      period.unrecorded > 0 && fill(text.t('opsPerformance.kpi.unrecorded'), { count: period.unrecorded, noun: text.night(period.unrecorded) }),
      period.unknown > 0 && fill(text.t('opsPerformance.kpi.unknown'), { count: period.unknown, noun: text.night(period.unknown) })
    ].filter(Boolean),
    note: (kpi, text) => text.t('opsPerformance.kpi.occupancyNote')
  }
};

const Period = ({ label, figure, lines, closedMonth }) => (
  <div className={closedMonth ? 'ops-kpi-period ops-kpi-period-closed' : 'ops-kpi-period'}>
    <div className="ops-kpi-period-label">{label}</div>
    {/* A figure with a % stays one left-to-right run inside an RTL card. */}
    <div className="ops-kpi-figure"><bdi dir="ltr">{figure}</bdi></div>
    {lines.map((line) => <div className="ops-kpi-line" key={line}>{line}</div>)}
  </div>
);

const Kpi = ({ kpi, months }) => {
  const { t, language } = useLanguage();
  const view = KPI_VIEW[kpi.key];
  if (!view) return null;
  const started = months.current.nightsCounted > 0;
  const title = t(view.title);
  const text = {
    t,
    review: (n) => nounFor(n, t('opsPerformance.kpi.reviewNoun'), language),
    night: (n) => nounFor(n, t('opsPerformance.kpi.nightNoun'), language)
  };
  return (
    <section className="ops-kpi" aria-label={title}>
      <h3 className="ops-kpi-title">{title}</h3>
      <div className="ops-kpi-periods">
        <Period
          closedMonth
          label={formatMonthName(months.last.month, language)}
          figure={view.figure(kpi.last)}
          lines={view.lines(kpi.last, text)}
        />
        <Period
          label={fill(t('opsPerformance.kpi.soFar'), { month: formatMonthName(months.current.month, language) })}
          figure={started ? view.figure(kpi.current) : NO_READING}
          lines={started ? view.lines(kpi.current, text) : [t('opsPerformance.kpi.startsTomorrow')]}
        />
      </div>
      <p className="ops-kpi-note">{view.note(kpi, text, view.figure)}</p>
    </section>
  );
};

const MonthlyKpiPanel = ({ data }) => (
  <div className="ops-kpi-sheet">
    {data.kpis.map((kpi) => <Kpi key={kpi.key} kpi={kpi} months={data.months} />)}
  </div>
);

export default MonthlyKpiPanel;
